import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Alert,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import GroupAddOutlinedIcon from '@mui/icons-material/GroupAddOutlined';
import CloseIcon from '@mui/icons-material/Close';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { useNavigate } from 'react-router-dom';
import TopNavbar from '../../components/TopNavBar/TopNavBar';
import UserPortalSideBar from '../../containers/UserSideBarContainer/UserSideBarContainer';
import { PageContainer, ContentContainer } from '../UserProfileInfoPage/UserProfileInfoPage.styles';
import {
  getBusinessSubUsers,
  createBusinessSubUser,
  updateBusinessSubUser,
  deactivateBusinessSubUser,
  businessTransferPrimary,
} from '../../api/api';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../components/ToastProvider/ToastProvider';

const emptyForm = { full_name: '', email: '', mobile_no: '', password: '' };
const emptyErrors = { full_name: '', email: '', mobile_no: '', password: '' };

const headCellSx = {
  fontWeight: 700,
  fontSize: '0.8125rem',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  color: 'text.secondary',
  borderBottom: '2px solid',
  borderColor: 'divider',
  py: 1.75,
};

export default function ManageBusinessUsersPage() {
  const theme = useTheme();
  const { showToast } = useToast();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState(emptyErrors);
  const [saving, setSaving] = useState(false);
  const [active, setActive] = useState(true);

  const [transferOpen, setTransferOpen] = useState(false);
  const [transferTargetId, setTransferTargetId] = useState('');
  const [transferReason, setTransferReason] = useState('');
  const [transferring, setTransferring] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getBusinessSubUsers();
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      showToast(e.message || 'Failed to load users', 'error');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditId(null);
    setForm(emptyForm);
    setErrors(emptyErrors);
    setActive(true);
    setOpen(true);
  };

  const openEdit = (row) => {
    setEditId(row.id);
    setForm({
      full_name: row.full_name || '',
      email: row.email || '',
      mobile_no: row.mobile_no || '',
      password: '',
    });
    setErrors(emptyErrors);
    setActive(row.is_active !== false);
    setOpen(true);
  };

  const validate = () => {
    const e = { ...emptyErrors };
    let ok = true;
    if (!form.full_name.trim()) {
      e.full_name = 'Full name is required';
      ok = false;
    }
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!form.email.trim()) {
      e.email = 'Email is required';
      ok = false;
    } else if (!emailPattern.test(form.email.trim())) {
      e.email = 'Enter a valid email address';
      ok = false;
    }
    if (!form.mobile_no.trim()) {
      e.mobile_no = 'Mobile number is required';
      ok = false;
    }
    if (!editId) {
      if (!form.password) {
        e.password = 'Password is required';
        ok = false;
      } else if (form.password.length < 8) {
        e.password = 'Minimum 8 characters';
        ok = false;
      }
    } else if (form.password && form.password.length < 8) {
      e.password = 'Minimum 8 characters';
      ok = false;
    }
    setErrors(e);
    return ok;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      if (editId) {
        const body = {
          full_name: form.full_name.trim(),
          email: form.email.trim(),
          mobile_no: form.mobile_no.trim(),
          is_active: active,
        };
        if (form.password && form.password.length >= 8) {
          body.password = form.password;
        }
        await updateBusinessSubUser(editId, body);
        showToast('User updated', 'success');
      } else {
        await createBusinessSubUser({
          full_name: form.full_name.trim(),
          email: form.email.trim(),
          mobile_no: form.mobile_no.trim(),
          password: form.password,
        });
        showToast('User created', 'success');
      }
      setOpen(false);
      load();
    } catch (e) {
      const msg =
        e?.data?.detail ||
        (typeof e?.data?.detail === 'string' ? e.data.detail : null) ||
        e.message ||
        'Save failed';
      showToast(String(msg), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (id) => {
    if (!window.confirm('Deactivate this user? They will not be able to sign in.')) return;
    try {
      await deactivateBusinessSubUser(id);
      showToast('User deactivated', 'success');
      load();
    } catch (e) {
      showToast(e.message || 'Failed', 'error');
    }
  };

  const activeMembers = rows.filter((r) => r.is_active);

  const openTransferDialog = () => {
    setTransferTargetId('');
    setTransferReason('');
    setTransferOpen(true);
  };

  const handleTransferPrimary = async () => {
    if (!transferTargetId) {
      showToast('Pick a team member to promote', 'warning');
      return;
    }
    if (!transferReason.trim()) {
      showToast('Please add a reason for the audit trail', 'warning');
      return;
    }
    const confirmMsg =
      'Transfer primary ownership?\n\n'
      + 'After this:\n'
      + '  • You become a regular company member.\n'
      + '  • Your current session will be signed out — you must sign in again.\n'
      + '  • The chosen user becomes the new primary and can manage company users.\n\n'
      + 'Continue?';
    if (!window.confirm(confirmMsg)) return;
    setTransferring(true);
    try {
      await businessTransferPrimary({
        new_primary_user_id: Number(transferTargetId),
        reason: transferReason.trim() || null,
        deactivate_old_primary: false,
      });
      showToast('Ownership transferred. Signing you out…', 'success');
      setTransferOpen(false);
      // Force session refresh — after sign-in again, this account will no
      // longer be a primary and the sidebar "Manage users" entry hides.
      try {
        await logout();
      } catch (_) {
        localStorage.removeItem('accessToken');
      }
      navigate('/login', { replace: true });
    } catch (err) {
      const msg =
        err?.data?.detail ||
        (typeof err?.data?.detail === 'string' ? err.data.detail : null) ||
        err.message ||
        'Transfer failed';
      showToast(String(msg), 'error');
    } finally {
      setTransferring(false);
    }
  };

  return (
    <>
      <TopNavbar />
      <PageContainer>
        <UserPortalSideBar />
        <ContentContainer
          sx={{
            flex: 1,
            minHeight: '100vh',
            bgcolor: alpha(theme.palette.primary.main, 0.02),
          }}
        >
          <Box
            sx={{
              maxWidth: 960,
              mx: 'auto',
              width: '100%',
              px: { xs: 2, sm: 3 },
              py: { xs: 2.5, sm: 4 },
            }}
          >
            {/* Page header */}
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2.5, sm: 3 },
                mb: 3,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
              }}
            >
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                alignItems={{ xs: 'stretch', sm: 'flex-start' }}
                justifyContent="space-between"
              >
                <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="h5"
                    component="h1"
                    sx={{ fontWeight: 700, color: 'text.primary', letterSpacing: '-0.02em' }}
                  >
                    Manage company users
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 560, lineHeight: 1.6 }}>
                    Invite colleagues who can sign in and place orders on behalf of your company.
                    Billing and shipping addresses stay on{' '}
                    <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      My Profile
                    </Box>
                    —only name, email, contact, and password are managed here.
                  </Typography>
                </Stack>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' } }}
                >
                  <Button
                    variant="outlined"
                    color="warning"
                    size="medium"
                    startIcon={<SwapHorizIcon />}
                    onClick={openTransferDialog}
                    disabled={activeMembers.length === 0}
                    sx={{
                      px: 2.5,
                      py: 1,
                      fontWeight: 600,
                      textTransform: 'none',
                      borderRadius: 1.5,
                    }}
                  >
                    Transfer primary
                  </Button>
                  <Button
                    variant="contained"
                    size="medium"
                    startIcon={<GroupAddOutlinedIcon />}
                    onClick={openCreate}
                    sx={{
                      px: 2.5,
                      py: 1,
                      fontWeight: 600,
                      textTransform: 'none',
                      borderRadius: 1.5,
                      boxShadow: 'none',
                      '&:hover': { boxShadow: 2 },
                    }}
                  >
                    Add user
                  </Button>
                </Stack>
              </Stack>
            </Paper>

            {/* Table card */}
            <Paper
              elevation={0}
              sx={{
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                overflow: 'hidden',
                bgcolor: 'background.paper',
              }}
            >
              <Box
                sx={{
                  px: 2.5,
                  py: 2,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  bgcolor: alpha(theme.palette.grey[500], 0.06),
                }}
              >
                <Typography variant="subtitle1" fontWeight={700}>
                  Team members
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {loading ? 'Loading…' : `${rows.length} user${rows.length !== 1 ? 's' : ''}`}
                </Typography>
              </Box>

              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 10 }}>
                  <CircularProgress size={40} thickness={4} />
                </Box>
              ) : rows.length === 0 ? (
                <Box
                  sx={{
                    py: 8,
                    px: 3,
                    textAlign: 'center',
                    maxWidth: 420,
                    mx: 'auto',
                  }}
                >
                  <Box
                    sx={{
                      width: 72,
                      height: 72,
                      borderRadius: '50%',
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      color: 'primary.main',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 2.5,
                    }}
                  >
                    <PersonOutlineIcon sx={{ fontSize: 40 }} />
                  </Box>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    No team members yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.65 }}>
                    Add people from your organization so they can log in, shop, and track their own orders
                    under your company account.
                  </Typography>
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<GroupAddOutlinedIcon />}
                    onClick={openCreate}
                    sx={{ textTransform: 'none', fontWeight: 600, px: 3, borderRadius: 1.5 }}
                  >
                    Add your first user
                  </Button>
                </Box>
              ) : (
                <TableContainer>
                  <Table size="medium" sx={{ minWidth: 640 }}>
                    <TableHead>
                      <TableRow
                        sx={{
                          bgcolor: alpha(theme.palette.grey[500], 0.08),
                          '& th': { borderBottom: 'none' },
                        }}
                      >
                        <TableCell sx={headCellSx}>Name</TableCell>
                        <TableCell sx={headCellSx}>Email</TableCell>
                        <TableCell sx={headCellSx}>Contact</TableCell>
                        <TableCell sx={headCellSx}>Status</TableCell>
                        <TableCell align="right" sx={{ ...headCellSx, pr: 2.5 }}>
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map((r) => (
                        <TableRow
                          key={r.id}
                          hover
                          sx={{
                            '&:last-child td': { borderBottom: 0 },
                            transition: 'background-color 0.15s ease',
                          }}
                        >
                          <TableCell sx={{ fontWeight: 600, py: 2 }}>{r.full_name || '—'}</TableCell>
                          <TableCell sx={{ py: 2, color: 'text.secondary' }}>{r.email}</TableCell>
                          <TableCell sx={{ py: 2, color: 'text.secondary' }}>{r.mobile_no || '—'}</TableCell>
                          <TableCell sx={{ py: 2 }}>
                            <Chip
                              size="small"
                              label={r.is_active ? 'Active' : 'Inactive'}
                              color={r.is_active ? 'success' : 'default'}
                              variant={r.is_active ? 'filled' : 'outlined'}
                              sx={{ fontWeight: 600, fontSize: '0.75rem' }}
                            />
                          </TableCell>
                          <TableCell align="right" sx={{ py: 2, pr: 2.5 }}>
                            <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap">
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => openEdit(r)}
                                sx={{ textTransform: 'none', fontWeight: 600 }}
                              >
                                Edit
                              </Button>
                              {r.is_active ? (
                                <Button
                                  size="small"
                                  variant="text"
                                  color="warning"
                                  onClick={() => handleDeactivate(r.id)}
                                  sx={{ textTransform: 'none', fontWeight: 600 }}
                                >
                                  Deactivate
                                </Button>
                              ) : null}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </Box>

          <Dialog
            open={open}
            onClose={() => setOpen(false)}
            maxWidth="sm"
            fullWidth
            PaperProps={{
              sx: { borderRadius: 2.5, overflow: 'hidden' },
            }}
          >
            {/* Header */}
            <DialogTitle
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 3,
                py: 2.25,
                bgcolor: alpha(theme.palette.primary.main, 0.04),
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Stack>
                <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.3 }}>
                  {editId ? 'Edit team member' : 'Add team member'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {editId
                    ? 'Update this user\'s details below.'
                    : 'Fill in all fields to create a new user.'}
                </Typography>
              </Stack>
              <IconButton
                aria-label="close"
                onClick={() => setOpen(false)}
                size="small"
                sx={{ ml: 2 }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </DialogTitle>

            {/* Form body */}
            <DialogContent sx={{ px: 3, pt: 3.5, pb: 2 }}>
              <Stack spacing={2.5}>
                <Typography
                  variant="overline"
                  color="text.secondary"
                  sx={{ letterSpacing: '0.08em', fontSize: '0.7rem', fontWeight: 700, mb: -0.5 }}
                >
                  Personal details
                </Typography>

                <TextField
                  label="Full name"
                  value={form.full_name}
                  onChange={(e) => {
                    setForm((f) => ({ ...f, full_name: e.target.value }));
                    if (errors.full_name) setErrors((prev) => ({ ...prev, full_name: '' }));
                  }}
                  fullWidth
                  required
                  autoFocus
                  error={Boolean(errors.full_name)}
                  helperText={errors.full_name}
                />

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                  <TextField
                    label="Email"
                    type="email"
                    value={form.email}
                    onChange={(e) => {
                      setForm((f) => ({ ...f, email: e.target.value }));
                      if (errors.email) setErrors((prev) => ({ ...prev, email: '' }));
                    }}
                    fullWidth
                    required
                    error={Boolean(errors.email)}
                    helperText={errors.email}
                  />
                  <TextField
                    label="Mobile number"
                    value={form.mobile_no}
                    onChange={(e) => {
                      setForm((f) => ({ ...f, mobile_no: e.target.value }));
                      if (errors.mobile_no) setErrors((prev) => ({ ...prev, mobile_no: '' }));
                    }}
                    fullWidth
                    required
                    error={Boolean(errors.mobile_no)}
                    helperText={errors.mobile_no}
                  />
                </Box>

                <Divider />

                <Typography
                  variant="overline"
                  color="text.secondary"
                  sx={{ letterSpacing: '0.08em', fontSize: '0.7rem', fontWeight: 700, mb: -0.5 }}
                >
                  Security
                </Typography>

                <TextField
                  label={editId ? 'New password' : 'Password'}
                  type="password"
                  value={form.password}
                  onChange={(e) => {
                    setForm((f) => ({ ...f, password: e.target.value }));
                    if (errors.password) setErrors((prev) => ({ ...prev, password: '' }));
                  }}
                  fullWidth
                  required={!editId}
                  error={Boolean(errors.password)}
                  helperText={
                    errors.password ||
                    (editId
                      ? 'Leave blank to keep the current password.'
                      : 'Minimum 8 characters. They will use this with their email to sign in.')
                  }
                />

                {editId ? (
                  <>
                    <Divider />
                    <Typography
                      variant="overline"
                      color="text.secondary"
                      sx={{ letterSpacing: '0.08em', fontSize: '0.7rem', fontWeight: 700, mb: -0.5 }}
                    >
                      Account status
                    </Typography>
                    <Paper
                      variant="outlined"
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 2,
                        py: 1.5,
                        borderRadius: 1.5,
                      }}
                    >
                      <Stack>
                        <Typography variant="body2" fontWeight={600}>
                          {active ? 'Account is active' : 'Account is deactivated'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {active
                            ? 'User can sign in and place orders.'
                            : 'User cannot sign in until reactivated.'}
                        </Typography>
                      </Stack>
                      <Switch
                        checked={active}
                        onChange={(e) => setActive(e.target.checked)}
                        color="primary"
                      />
                    </Paper>
                  </>
                ) : null}
              </Stack>
            </DialogContent>

            {/* Footer */}
            <DialogActions
              sx={{
                px: 3,
                py: 2,
                borderTop: '1px solid',
                borderColor: 'divider',
                bgcolor: alpha(theme.palette.grey[500], 0.04),
                gap: 1,
              }}
            >
              <Button
                onClick={() => setOpen(false)}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                  borderRadius: 1.5,
                  boxShadow: 'none',
                  '&:hover': { boxShadow: 2 },
                }}
              >
                {saving ? 'Saving…' : editId ? 'Save changes' : 'Create user'}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Transfer primary ownership — self-serve */}
          <Dialog
            open={transferOpen}
            onClose={() => !transferring && setTransferOpen(false)}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { borderRadius: 2.5, overflow: 'hidden' } }}
          >
            <DialogTitle
              sx={{
                px: 3,
                py: 2.25,
                bgcolor: alpha(theme.palette.warning.main, 0.06),
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Stack>
                <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.3 }}>
                  Transfer primary ownership
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Promote a team member to primary. You become a regular member.
                </Typography>
              </Stack>
            </DialogTitle>
            <DialogContent sx={{ px: 3, pt: 3, pb: 2 }}>
              <Alert severity="warning" sx={{ mb: 2 }}>
                Once transferred, you will be signed out and must sign in again.
                Your account will keep working as a company <strong>member</strong>,
                but you will no longer be able to add, edit, or deactivate team
                members.
              </Alert>

              <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                <InputLabel id="self-new-primary-label">New primary (pick an active team member)</InputLabel>
                <Select
                  labelId="self-new-primary-label"
                  label="New primary (pick an active team member)"
                  value={transferTargetId}
                  onChange={(e) => setTransferTargetId(e.target.value)}
                >
                  {activeMembers.length === 0 && (
                    <MenuItem value="" disabled>
                      No active team members — add one first
                    </MenuItem>
                  )}
                  {activeMembers.map((m) => (
                    <MenuItem key={m.id} value={m.id}>
                      {m.full_name || m.email} — {m.email}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Reason (audit trail)"
                value={transferReason}
                onChange={(e) => setTransferReason(e.target.value)}
                placeholder="e.g. Leaving the company / handover"
                required
                fullWidth
                size="small"
                multiline
                minRows={2}
                sx={{ mt: 2 }}
                helperText="Required — this is permanently recorded in the ownership history"
              />
            </DialogContent>
            <DialogActions
              sx={{
                px: 3,
                py: 2,
                borderTop: '1px solid',
                borderColor: 'divider',
                bgcolor: alpha(theme.palette.grey[500], 0.04),
                gap: 1,
              }}
            >
              <Button
                onClick={() => setTransferOpen(false)}
                disabled={transferring}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="warning"
                startIcon={<SwapHorizIcon />}
                onClick={handleTransferPrimary}
                disabled={transferring || !transferTargetId || !transferReason.trim()}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                  borderRadius: 1.5,
                }}
              >
                {transferring ? 'Transferring…' : 'Transfer & sign out'}
              </Button>
            </DialogActions>
          </Dialog>
        </ContentContainer>
      </PageContainer>
    </>
  );
}
